import { useRef, useState } from 'react';

// Compartido entre TicketDetalle.tsx (ticket ya existe, sube de inmediato) y
// NuevoTicketModal.tsx (ticket no existe aún, solo selecciona localmente) —
// evita duplicar el dropzone/lista/validación entre ambos.

export const MAX_ADJUNTO_BYTES = 10 * 1024 * 1024;

// Whitelist best-effort por extensión — la validación autoritativa (magic
// bytes) solo puede correr en el backend; esto es únicamente feedback
// temprano en el preview local antes de subir.
const EXTENSIONES_PERMITIDAS = ['.png', '.jpg', '.jpeg', '.webp', '.gif', '.pdf', '.docx', '.xlsx', '.txt'];

export function fmtTamano(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function iconoPorMime(mime: string | null | undefined): string {
  const m = mime ?? '';
  if (m.startsWith('image/')) return '🖼️';
  if (m === 'application/pdf') return '📕';
  if (m === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') return '📄';
  if (m === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') return '📊';
  if (m === 'text/plain') return '📝';
  return '📎';
}

// null = válido a nivel best-effort. El backend puede rechazarlo igual si el
// contenido real no coincide con la extensión (detección por magic bytes).
export function validarArchivoLocal(file: File): string | null {
  if (file.size > MAX_ADJUNTO_BYTES) {
    return `Excede el límite de ${MAX_ADJUNTO_BYTES / (1024 * 1024)}MB`;
  }
  const punto = file.name.lastIndexOf('.');
  const ext = punto >= 0 ? file.name.slice(punto).toLowerCase() : '';
  if (!EXTENSIONES_PERMITIDAS.includes(ext)) {
    return 'Tipo de archivo no permitido';
  }
  return null;
}

interface DropzoneArchivoProps {
  onFilesSelected: (files: File[]) => void;
  disabled?: boolean;
  subiendo?: boolean;
  progreso?: number;
  label?: string;
}

export function DropzoneArchivo({ onFilesSelected, disabled, subiendo, progreso, label }: DropzoneArchivoProps) {
  const [arrastrando, setArrastrando] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  function seleccionar(files: FileList | null) {
    if (!files || !files.length) return;
    onFilesSelected(Array.from(files));
  }

  return (
    <>
      <div
        onDragOver={e => {
          e.preventDefault();
          if (!disabled) setArrastrando(true);
        }}
        onDragLeave={() => setArrastrando(false)}
        onDrop={e => {
          e.preventDefault();
          setArrastrando(false);
          if (!disabled) seleccionar(e.dataTransfer.files);
        }}
        onClick={() => !disabled && fileInputRef.current?.click()}
        style={{
          border: `1.5px dashed ${arrastrando ? 'var(--brand)' : 'var(--border)'}`,
          borderRadius: 8,
          padding: '14px 10px',
          textAlign: 'center',
          fontSize: 12,
          color: 'var(--muted)',
          cursor: disabled ? 'default' : 'pointer',
          background: arrastrando ? 'var(--card-hl)' : 'transparent',
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {subiendo ? `Subiendo… ${progreso ?? 0}%` : (label ?? `Arrastra un archivo aquí o haz clic para elegirlo (máx. ${MAX_ADJUNTO_BYTES / (1024 * 1024)}MB)`)}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: 'none' }}
          disabled={disabled}
          onChange={e => {
            seleccionar(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {subiendo && (
        <div style={{ height: 4, background: '#e2e8f0', borderRadius: 2, marginTop: 6, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${progreso ?? 0}%`, background: 'var(--brand)', transition: 'width .15s' }} />
        </div>
      )}
    </>
  );
}
